import { useEffect } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";

type Locale = "en" | "ar";

type TableRow = {
  category: string;
  data: string;
  purpose: string;
  sharedWith: string;
};

type SectionBlock = {
  title: string;
  intro?: string;
  items?: string[];
  outro?: string;
  body?: string;
  contact?: {
    organisation: string;
    email: string;
    website: string;
    location: string;
  };
};

type ThirdPartyItem =
  | string
  | {
      name: string;
      desc: string;
      url: string;
      label: string;
    };

type PolicyContent = {
  dir: "ltr" | "rtl";
  brand: string;
  brandSub: string;
  heroTitle: string;
  heroMeta: string;
  callout: string;
  tableHeaders: [string, string, string, string];
  tableRows: TableRow[];
  sections: {
    intro: SectionBlock;
    dataCollected: SectionBlock;
    howWeUse: SectionBlock;
    legalBasis: SectionBlock;
    thirdParty: {
      title: string;
      intro: string;
      items: ThirdPartyItem[];
      outro: string;
    };
    retention: SectionBlock;
    security: SectionBlock;
    rights: SectionBlock;
    children: SectionBlock;
    changes: SectionBlock;
    contact: SectionBlock;
  };
  footerNote: string;
};

const content: Record<Locale, PolicyContent> = {
  en: {
    dir: "ltr",
    brand: "Al Murafiq",
    brandSub: "Mobile privacy policy",
    heroTitle: "Privacy Policy",
    heroMeta: "Last updated: 11 June 2026",
    callout:
      "This Privacy Policy explains how Al Murafiq collects, uses, stores, and shares personal information when you use the mobile application on iOS and Android.",
    tableHeaders: ["Category", "What we collect", "Why we collect it", "Shared with"],
    tableRows: [
      {
        category: "Account and sign-in information",
        data: "Email address used to request sign-in access, the one-time verification code flow, and vendor profile details returned by our backend such as name, email, phone number, account type, and user identifier.",
        purpose: "Authenticate vendor users and maintain secure sessions.",
        sharedWith: "EMCODE TECHNOLOGY L.L.C. backend services.",
      },
      {
        category: "Operational data",
        data: "Orders and shipment details, vehicle and plate-related information, lock assignment history, unlock request history, and route and tracking data associated with orders.",
        purpose: "Display ongoing and completed orders, support lock workflows, and show route and tracking records.",
        sharedWith: "EMCODE TECHNOLOGY L.L.C. backend services, and where required for compliance and safety, Dubai Police, SIRA, or other competent authorities.",
      },
      {
        category: "Audit and activity logs",
        data: "Login activity, scan activity, lock assignment actions, unlock requests, timestamps, user ID, and workflow events recorded for vendor traceability.",
        purpose: "Maintain a traceable record of vendor actions for operational oversight, compliance, and investigations.",
        sharedWith: "EMCODE TECHNOLOGY L.L.C. backend services, and where required, compliance, security, or authority recipients.",
      },
      {
        category: "Camera data",
        data: "QR code scans used to identify locks or orders, and plate photos captured for license plate recognition.",
        purpose: "Scan QR codes and vehicle plates when you use the camera features.",
        sharedWith: "Plate Recognizer when you capture plate photos for recognition.",
      },
      {
        category: "Local app data",
        data: "Secure session tokens stored on your device, local language preference, local theme preference, and a first-launch flag used to clear stale credentials after reinstall.",
        purpose: "Keep you signed in, remember your settings, and clear stale credentials after reinstall.",
        sharedWith: "Stored on your device using secure storage and local app state.",
      },
      {
        category: "Route and tracking data",
        data: "Route and tracking information associated with your orders, including map and route views generated from backend data.",
        purpose: "Render maps and route views for order details.",
        sharedWith: "Mapbox and backend systems that supply the order data.",
      },
    ],
    sections: {
      intro: {
        title: "1. Scope",
        body:
          "This policy applies to the Al Murafiq mobile app used by vendor users to sign in, view and manage orders, scan QR codes and vehicle plates, and perform lock assignment or unlock workflows. The platform is used for regulated transportation operations, including dangerous goods and fireworks movement, and is not a general-purpose logistics app.",
      },
      dataCollected: {
        title: "2. Information We Collect",
        intro:
          "We collect only the information needed to run the service.",
      },
      howWeUse: {
        title: "3. How We Use Your Information",
        intro: "We use the information we collect to:",
        items: [
          "Authenticate vendor users and maintain secure sessions.",
          "Display ongoing and completed orders, shipment details, and related operational records.",
          "Support lock assignment and unlock workflows.",
          "Read QR codes and vehicle plates when you use the camera features.",
          "Record audit logs so vendor actions remain traceable, including login activity, scan activity, lock assignment, unlock requests, timestamps, user ID, and related workflow actions.",
          "Share operational data with Dubai Police, SIRA, or other authorities for compliance and safety where required by law.",
          "Render maps and route views for order details.",
          "Remember your language and theme preferences on the device.",
          "Keep the app secure, stable, and usable.",
          "Comply with applicable legal and operational requirements.",
        ],
        outro:
          "We do not use your information for advertising, resale, or unrelated profiling.",
      },
      legalBasis: {
        title: "4. Camera, Maps, and Third-Party Services",
        intro: "The app requests camera access because it needs to scan QR codes and vehicle plates. The vendor app does not provide access to live camera feeds.",
        items: [
          "When you capture a plate photo for recognition, the image is sent to Plate Recognizer so the plate number can be read automatically.",
          "Order maps and route visuals are rendered using Mapbox.",
          "The app uses the map service only for the data required to display the map view associated with your orders.",
          "Email delivery for sign-in codes is handled by the backend through email infrastructure.",
        ],
        outro: "The app does not use advertising identifiers for profiling or ad targeting.",
      },
      thirdParty: {
        title: "5. Location Data",
        intro:
          "The app does not request or use continuous live GPS tracking from the device in the current codebase.",
        items: [
          "Map and route views are based on order data supplied by our backend, not on background location collection from your device.",
        ],
        outro:
          "The app does not perform continuous background GPS tracking in the current codebase.",
      },
      retention: {
        title: "6. Storage and Retention",
        body:
          "Session tokens are stored securely on the device using the platform secure storage mechanism. Language and theme preferences are stored locally so the app can remember your settings. When you sign out, local authentication data is cleared from the device. We keep order, account, audit-log, and operational data only as long as needed to operate the service, maintain business records, and meet legal, regulatory, audit, investigation, security, or authority requirements.",
      },
      security: {
        title: "7. Security",
        intro:
          "We use reasonable technical and organizational measures to protect your information, including:",
        items: [
          "Encrypted network connections for app-to-backend communication.",
          "Secure local storage for session tokens.",
          "Access controls that limit operational data to authorized systems and staff.",
          "Ongoing maintenance to reduce avoidable security issues.",
        ],
        outro:
          "No security system is perfect, and we cannot guarantee absolute protection.",
      },
      rights: {
        title: "8. Your Rights",
        intro: "Depending on applicable law, you may have the right to:",
        items: [
          "Access the personal data we hold about you.",
          "Ask us to correct inaccurate or incomplete information.",
          "Request deletion of your personal data, subject to legal and operational retention requirements.",
          "Withdraw camera access through your device settings if you no longer want to scan QR codes or plates.",
          "Contact us if you believe your account was accessed without authorization.",
        ],
        outro: "",
      },
      children: {
        title: "9. Changes to This Policy",
        body:
          "We may update this Privacy Policy when the app changes or when legal requirements change. If we make material changes, we will update the date at the top of this document.",
      },
      changes: {
        title: "10. Contact Us",
        body:
          "If you have a privacy question, a correction request, or a deletion request, contact us using the details below.",
      },
      contact: {
        title: "Contact Details",
        intro: "EMCODE TECHNOLOGY L.L.C. operates Al Murafiq from Dubai, United Arab Emirates.",
        contact: {
          organisation: "EMCODE TECHNOLOGY L.L.C.",
          email: "support@emcode.ae",
          website: "www.emcode.ae",
          location: "Dubai Design District (d3)\nOffice 608 B1B & 108A B6\nDubai, UAE",
        },
      },
    },
    footerNote: "This Privacy Policy is governed by and construed in accordance with the laws of the United Arab Emirates.",
  },
  ar: {
    dir: "rtl",
    brand: "Al Murafiq",
    brandSub: "سياسة الخصوصية للتطبيق",
    heroTitle: "سياسة الخصوصية",
    heroMeta: "آخر تحديث: 11 يونيو 2026",
    callout:
      "توضح سياسة الخصوصية هذه كيفية جمع واستخدام وتخزين ومشاركة المعلومات الشخصية عند استخدام تطبيق Al Murafiq على iOS وAndroid.",
    tableHeaders: ["الفئة", "ما نجمعه", "سبب الجمع", "تُشارك مع"],
    tableRows: [
      {
        category: "بيانات الحساب وتسجيل الدخول",
        data: "عنوان البريد الإلكتروني المستخدم لطلب الدخول، ومسار رمز التحقق لمرة واحدة، وبيانات الملف الشخصي للمورّد التي تُرجعها واجهة الخلفية مثل الاسم والبريد الإلكتروني ورقم الهاتف ونوع الحساب ومعرّف المستخدم.",
        purpose: "مصادقة مستخدمي المورّدين والحفاظ على جلسات آمنة.",
        sharedWith: "خدمات الواجهة الخلفية التابعة لشركة EMCODE TECHNOLOGY L.L.C.",
      },
      {
        category: "البيانات التشغيلية",
        data: "تفاصيل الطلبات أو الشحنات، ومعلومات المركبة واللوحة، وسجل إسناد الأقفال، وسجل طلبات الفتح، وبيانات المسار والتتبع المرتبطة بالطلبات.",
        purpose: "عرض الطلبات الجارية والمكتملة، ودعم سير عمل الأقفال، وعرض سجلات المسارات والتتبع.",
        sharedWith: "خدمات الواجهة الخلفية التابعة لشركة EMCODE TECHNOLOGY L.L.C.، وعند الاقتضاء للامتثال والسلامة، قد تُشارك مع شرطة دبي أو سِرَا أو أي جهة مختصة أخرى.",
      },
      {
        category: "سجلات التدقيق والنشاط",
        data: "نشاط تسجيل الدخول، ونشاط المسح، وإجراءات إسناد الأقفال، وطلبات الفتح، والطوابع الزمنية، ومعرّف المستخدم، وأحداث سير العمل المسجلة لإمكانية تتبع تصرفات المورّد.",
        purpose: "الاحتفاظ بسجل يمكن تتبعه لتصرفات المورّدين لأغراض الرقابة التشغيلية والامتثال والتحقيقات.",
        sharedWith: "خدمات الواجهة الخلفية التابعة لشركة EMCODE TECHNOLOGY L.L.C.، وعند الاقتضاء الجهات المرتبطة بالامتثال أو الأمن أو السلطات المختصة.",
      },
      {
        category: "بيانات الكاميرا",
        data: "مسوحات رمز QR المستخدمة لتحديد الأقفال أو الطلبات، وصور اللوحات الملتقطة للتعرّف على اللوحات.",
        purpose: "مسح رموز QR ولوحات المركبات عند استخدام ميزات الكاميرا.",
        sharedWith: "Plate Recognizer عند التقاط صور اللوحات للتعرّف عليها.",
      },
      {
        category: "بيانات التطبيق المحلية",
        data: "رموز جلسة آمنة مخزنة على جهازك، وتفضيل اللغة المحلية، وتفضيل الثيم المحلي، وعلامة أول تشغيل تُستخدم لمسح بيانات الاعتماد القديمة بعد إعادة التثبيت.",
        purpose: "إبقاؤك مسجلاً للدخول وتذكّر إعداداتك ومسح بيانات الاعتماد القديمة بعد إعادة التثبيت.",
        sharedWith: "تُخزن على جهازك باستخدام التخزين الآمن وحالة التطبيق المحلية.",
      },
      {
        category: "بيانات المسار والتتبع",
        data: "بيانات المسار والتتبع المرتبطة بطلباتك، بما في ذلك طرق الخرائط وعرض المسار الناتجة من بيانات الخلفية.",
        purpose: "عرض الخرائط وواجهات المسار لتفاصيل الطلب.",
        sharedWith: "Mapbox والأنظمة الخلفية التي تزود بيانات الطلب.",
      },
    ],
    sections: {
      intro: {
        title: "١. النطاق",
        body:
          "تنطبق هذه السياسة على تطبيق Al Murafiq للجوال المستخدم من قبل المورّدين لتسجيل الدخول، وعرض وإدارة الطلبات، ومسح رموز QR ولوحات المركبات، وتنفيذ مهام إسناد الأقفال أو فتحها. تُستخدم المنصة لعمليات نقل منظمة تشمل المواد الخطرة والألعاب النارية، وليست تطبيقاً لوجستياً عاماً.",
      },
      dataCollected: {
        title: "٢. المعلومات التي نجمعها",
        intro: "نجمع فقط المعلومات اللازمة لتشغيل الخدمة.",
      },
      howWeUse: {
        title: "٣. كيف نستخدم معلوماتك",
        intro: "نستخدم المعلومات التي نجمعها من أجل:",
        items: [
          "مصادقة مستخدمي المورّدين والحفاظ على جلسات آمنة.",
          "عرض الطلبات الجارية والمكتملة وتفاصيل الشحن والسجلات التشغيلية ذات الصلة.",
          "دعم مهام إسناد الأقفال وفتحها.",
          "قراءة رموز QR ولوحات المركبات عند استخدام ميزات الكاميرا.",
          "تسجيل سجلات تدقيق حتى تبقى تصرفات المورّدين قابلة للتتبع، بما في ذلك نشاط تسجيل الدخول، ونشاط المسح، وإجراءات إسناد الأقفال، وطلبات الفتح، والطوابع الزمنية، ومعرّف المستخدم، وأحداث سير العمل ذات الصلة.",
          "قد تُشارك البيانات التشغيلية مع شرطة دبي أو سِرَا أو أي جهة مختصة أخرى لأغراض الامتثال والسلامة عند الاقتضاء بموجب القانون.",
          "عرض الخرائط وواجهات المسار لتفاصيل الطلب.",
          "تذكّر تفضيلات اللغة والثيم على الجهاز.",
          "الحفاظ على أمان التطبيق واستقراره وقابليته للاستخدام.",
          "الامتثال للمتطلبات القانونية والتشغيلية المعمول بها.",
        ],
        outro:
          "لا نستخدم معلوماتك للإعلانات أو لإعادة البيع أو لإنشاء ملفات تعريف غير مرتبطة بالخدمة.",
      },
      legalBasis: {
        title: "٤. الكاميرا والخرائط والخدمات الخارجية",
        intro: "يطلب التطبيق إذن الكاميرا لأنه يحتاج إلى مسح رموز QR ولوحات المركبات. ولا يوفّر تطبيق المورّد الوصول إلى بث مباشر من الكاميرا.",
        items: [
          "عند التقاط صورة لوحة للتعرّف عليها، تُرسل الصورة إلى Plate Recognizer حتى يمكن قراءة رقم اللوحة تلقائياً.",
          "تُعرض خرائط الطلب وواجهات المسار باستخدام Mapbox.",
          "يستخدم التطبيق خدمة الخرائط فقط للبيانات المطلوبة لعرض الخريطة المرتبطة بطلباتك.",
          "يتم التعامل مع تسليم رسائل رمز الدخول من خلال الخلفية وبنية البريد الإلكتروني.",
        ],
        outro: "لا يستخدم التطبيق معرّفات إعلانية للتنميط أو الاستهداف الإعلاني.",
      },
      thirdParty: {
        title: "٥. بيانات الموقع",
        intro:
          "لا يطلب التطبيق أو يستخدم تتبع GPS مباشر ومستمر من الجهاز في قاعدة الكود الحالية.",
        items: [
          "تستند الخرائط وواجهات المسار إلى بيانات الطلب التي توفرها الخلفية، وليس إلى جمع الموقع في الخلفية من جهازك.",
        ],
        outro:
          "لا ينفذ التطبيق تتبع GPS في الخلفية بشكل مستمر في قاعدة الكود الحالية.",
      },
      retention: {
        title: "٦. التخزين والاحتفاظ",
        body:
          "تُخزن رموز الجلسة بشكل آمن على الجهاز باستخدام آلية التخزين الآمن الخاصة بالنظام الأساسي. تُحفظ تفضيلات اللغة والثيم محلياً حتى يتذكر التطبيق إعداداتك. عند تسجيل الخروج، تُمسح بيانات الاعتماد المحلية من الجهاز. نحتفظ ببيانات الطلبات والحساب وسجلات التدقيق والبيانات التشغيلية فقط طالما كان ذلك ضرورياً لتشغيل الخدمة والاحتفاظ بسجلات الأعمال والوفاء بمتطلبات قانونية أو تنظيمية أو تدقيقية أو تحقيقية أو أمنية أو خاصة بالجهات المختصة.",
      },
      security: {
        title: "٧. الأمان",
        intro:
          "نستخدم تدابير تقنية وتنظيمية معقولة لحماية معلوماتك، بما في ذلك:",
        items: [
          "اتصالات شبكة مشفرة بين التطبيق والخلفية.",
          "تخزين محلي آمن لرموز الجلسة.",
          "ضوابط وصول تحد من البيانات التشغيلية على الأنظمة والموظفين المخولين فقط.",
          "صيانة مستمرة لتقليل مشكلات الأمان القابلة للتجنب.",
        ],
        outro:
          "لا يوجد نظام أمان مثالي، ولا يمكننا ضمان الحماية المطلقة.",
      },
      rights: {
        title: "٨. حقوقك",
        intro: "وفقاً للقانون المطبق، قد يكون لك الحق في:",
        items: [
          "الاطلاع على البيانات الشخصية التي نحتفظ بها عنك.",
          "طلب تصحيح المعلومات غير الدقيقة أو غير الكاملة.",
          "طلب حذف بياناتك الشخصية، مع مراعاة متطلبات الاحتفاظ القانونية والتشغيلية.",
          "سحب إذن الكاميرا من إعدادات جهازك إذا لم تعد تريد مسح رموز QR أو اللوحات.",
          "التواصل معنا إذا كنت تعتقد أن حسابك تم الوصول إليه دون إذن.",
        ],
        outro: "",
      },
      children: {
        title: "٩. التغييرات على هذه السياسة",
        body:
          "قد نحدّث سياسة الخصوصية هذه عندما يتغير التطبيق أو عندما تتغير المتطلبات القانونية. إذا أجرينا تغييرات جوهرية، فسنحدّث التاريخ الموجود أعلى هذه الوثيقة.",
      },
      changes: {
        title: "١٠. تواصل معنا",
        body: "إذا كانت لديك أسئلة حول الخصوصية أو طلب تصحيح أو حذف، فتواصل معنا باستخدام البيانات أدناه.",
      },
      contact: {
        title: "تفاصيل الاتصال",
        intro: "تدير EMCODE TECHNOLOGY L.L.C. تطبيق Al Murafiq من دبي، الإمارات العربية المتحدة.",
        contact: {
          organisation: "EMCODE TECHNOLOGY L.L.C.",
          email: "support@emcode.ae",
          website: "www.emcode.ae",
          location: "منطقة دبي للتصميم (d3)\nالمكتب 608 B1B و108A B6\nدبي، الإمارات العربية المتحدة",
        },
      },
    },
    footerNote:
      "تخضع سياسة الخصوصية هذه لقوانين دولة الإمارات العربية المتحدة وتُفسَّر وفقاً لها.",
  },
};

const shellStyle: CSSProperties = {
  background:
    "radial-gradient(circle at 14% 20%, rgba(46, 157, 88, 0.16), transparent 22%), radial-gradient(circle at 82% 8%, rgba(8, 92, 50, 0.12), transparent 18%), linear-gradient(180deg, #f5faf6 0%, #edf5ee 42%, #e8f1ea 100%)",
  color: "var(--text-main)",
};

const glassBorder = "1px solid rgba(46, 157, 88, 0.14)";

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      className="rounded-[28px] bg-white/90 p-5 shadow-[0_20px_45px_rgba(15,55,32,0.07)] backdrop-blur-sm sm:p-6"
      style={{ border: glassBorder }}
    >
      <h2 className="mb-4 text-[1.05rem] font-extrabold tracking-tight text-[#15452b] sm:text-[1.15rem]">
        {title}
      </h2>
      {children}
    </section>
  );
}

function PrivacyPolicyPage() {
  const { i18n } = useTranslation();
  const resolvedLanguage = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
  const locale: Locale = resolvedLanguage.startsWith("ar") ? "ar" : "en";
  const c = content[locale];
  const s = c.sections;

  useEffect(() => {
    document.title =
      locale === "ar" ? "سياسة الخصوصية | المرافق" : "Privacy Policy | Al Murafiq";
  }, [locale]);

  return (
    <div dir={c.dir} lang={locale} className="relative h-screen overflow-hidden" style={shellStyle}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#0e5f36]/10 to-transparent" />
        <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-[#2d9b57]/10 blur-3xl" />
        <div className="absolute right-[-6rem] top-40 h-80 w-80 rounded-full bg-[#0f6b3a]/10 blur-3xl" />
      </div>

      <div className="relative flex h-full flex-col">
        <header
          className="border-b px-4 py-4 sm:px-6 lg:px-8"
          style={{
            background:
              "linear-gradient(135deg, #0c3b24 0%, #0f5c35 48%, #17804a 100%)",
            borderColor: "rgba(255,255,255,0.08)",
          }}
        >
          <div className="mx-auto flex w-full max-w-6xl items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/10">
              <img
                src="/assets/images/murafiq_logo.png"
                alt="Al Murafiq logo"
                className="h-9 w-9 object-contain"
              />
            </div>
            <div className="min-w-0">
              <div className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-[#bde9c8]">
                {c.brandSub}
              </div>
              <div className="mt-1 text-2xl font-black tracking-tight text-white sm:text-3xl">
                {c.brand}
              </div>
            </div>
            <div className="ms-auto hidden rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-white/85 md:block">
              {c.heroTitle}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <section
              className="overflow-hidden rounded-[32px] border bg-white/90 shadow-[0_30px_60px_rgba(12,56,31,0.08)] backdrop-blur-sm"
              style={{ borderColor: "rgba(20, 112, 61, 0.15)" }}
            >
              <div className="grid gap-0 lg:grid-cols-[1.15fr_0.85fr]">
                <div className="relative p-6 sm:p-8 lg:p-10">
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "radial-gradient(circle at top left, rgba(40,160,86,0.12), transparent 42%), radial-gradient(circle at bottom right, rgba(14,92,55,0.08), transparent 35%)",
                    }}
                  />
                  <div className="relative">
                    <div className="inline-flex items-center rounded-full border border-[#bce8c8] bg-[#effaf2] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[#16613a]">
                      {locale === "ar" ? "تطبيق الجوال" : "Mobile app"}
                    </div>
                    <h1 className="mt-4 text-4xl font-black tracking-tight text-[#124227] sm:text-5xl">
                      {c.heroTitle}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm leading-7 text-[#4a6456] sm:text-base">
                      {c.heroMeta}
                    </p>
                    <div className="mt-6 rounded-2xl border border-[#cfe8d5] bg-[#f4fbf6] p-4 text-sm leading-7 text-[#335543]">
                      {c.callout}
                    </div>
                  </div>
                </div>

                <div className="border-t border-[#dfece3] bg-gradient-to-br from-[#0d5b34] via-[#12733f] to-[#1a8e4d] p-6 text-white lg:border-l lg:border-t-0 lg:p-10">
                  <div className="text-xs font-semibold uppercase tracking-[0.28em] text-white/75">
                    {locale === "ar" ? "نطاق السياسة" : "Policy scope"}
                  </div>
                  <div className="mt-4 text-2xl font-black tracking-tight">
                    {locale === "ar"
                      ? "البيانات التي نحتاجها لتشغيل التطبيق"
                      : "The data we need to run the app"}
                  </div>
                  <p className="mt-4 text-sm leading-7 text-white/86">
                    {locale === "ar"
                      ? "يغطي هذا المستند بيانات الحساب والوثائق وطلبات الخدمة والخرائط والأذونات المحلية التي يعتمد عليها التطبيق."
                      : "This document covers account data, documents, service requests, maps, and the local storage used by the app."}
                  </p>
                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    {[
                      locale === "ar" ? "تسجيل الدخول بالرمز" : "OTP login",
                      locale === "ar" ? "الخرائط والبحث" : "Maps and search",
                      locale === "ar" ? "رفع المستندات" : "Document uploads",
                      locale === "ar" ? "التتبع والتحديثات" : "Tracking and updates",
                    ].map((item) => (
                      <div
                        key={item}
                        className="rounded-2xl border border-white/12 bg-white/10 px-4 py-3 text-sm font-semibold text-white/92"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <SectionCard title={s.intro.title}>
              <p className="text-[0.98rem] leading-8 text-[#4f6557]">{s.intro.body}</p>
            </SectionCard>

            <SectionCard title={s.dataCollected.title}>
              <p className="mb-5 text-[0.98rem] leading-8 text-[#4f6557]">{s.dataCollected.intro}</p>
              <div className="overflow-x-auto rounded-2xl border border-[#dbe9de] bg-white">
                <table className="min-w-[860px] w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="bg-[#104126] text-white">
                      {c.tableHeaders.map((header) => (
                        <th
                          key={header}
                          className="px-4 py-4 text-start text-xs font-bold uppercase tracking-[0.18em]"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {c.tableRows.map((row, index) => (
                      <tr
                        key={row.category}
                        className={index % 2 === 0 ? "bg-[#f7fbf7]" : "bg-white"}
                      >
                        <td className="border-t border-[#dbe9de] px-4 py-4 align-top font-bold text-[#15452b]">
                          {row.category}
                        </td>
                        <td className="border-t border-[#dbe9de] px-4 py-4 align-top leading-7 text-[#506457]">
                          {row.data}
                        </td>
                        <td className="border-t border-[#dbe9de] px-4 py-4 align-top leading-7 text-[#506457]">
                          {row.purpose}
                        </td>
                        <td className="border-t border-[#dbe9de] px-4 py-4 align-top leading-7 text-[#506457]">
                          {row.sharedWith}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title={s.howWeUse.title}>
              {s.howWeUse.intro && (
                <p className="mb-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.howWeUse.intro}</p>
              )}
              {s.howWeUse.items && (
                <ul className="space-y-3 text-[0.98rem] leading-8 text-[#4f6557]">
                  {s.howWeUse.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-[#22a65b]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.howWeUse.outro && (
                <p className="mt-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.howWeUse.outro}</p>
              )}
            </SectionCard>

            <SectionCard title={s.legalBasis.title}>
              {s.legalBasis.intro && (
                <p className="mb-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.legalBasis.intro}</p>
              )}
              {s.legalBasis.items && (
                <ul className="space-y-3 text-[0.98rem] leading-8 text-[#4f6557]">
                  {s.legalBasis.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-[#22a65b]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            <SectionCard title={s.thirdParty.title}>
              <p className="mb-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.thirdParty.intro}</p>
              <ul className="space-y-3 text-[0.98rem] leading-8 text-[#4f6557]">
                {s.thirdParty.items.map((item) => (
                  typeof item === "string" ? (
                    <li key={item} className="flex gap-3">
                      <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-[#22a65b]" />
                      <span>{item}</span>
                    </li>
                  ) : (
                    <li key={item.name}>
                      <span className="font-bold text-[#15452b]">{item.name}</span>{" "}
                      <span>{item.desc}</span>{" "}
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-[#138144] underline decoration-[#8fd7a9] underline-offset-4 transition-colors hover:text-[#0d6b38]"
                      >
                        {item.label}
                      </a>
                      .
                    </li>
                  )
                ))}
              </ul>
              <p className="mt-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.thirdParty.outro}</p>
            </SectionCard>

            <SectionCard title={s.retention.title}>
              {s.retention.body && (
                <p className="text-[0.98rem] leading-8 text-[#4f6557]">{s.retention.body}</p>
              )}
            </SectionCard>

            <SectionCard title={s.security.title}>
              {s.security.intro && (
                <p className="mb-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.security.intro}</p>
              )}
              {s.security.items && (
                <ul className="space-y-3 text-[0.98rem] leading-8 text-[#4f6557]">
                  {s.security.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-[#22a65b]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.security.outro && (
                <p className="mt-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.security.outro}</p>
              )}
            </SectionCard>

            <SectionCard title={s.rights.title}>
              {s.rights.intro && (
                <p className="mb-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.rights.intro}</p>
              )}
              {s.rights.items && (
                <ul className="space-y-3 text-[0.98rem] leading-8 text-[#4f6557]">
                  {s.rights.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-[#22a65b]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.rights.outro && (
                <p className="mt-4 text-[0.98rem] leading-8 text-[#4f6557]">{s.rights.outro}</p>
              )}
            </SectionCard>

            <SectionCard title={s.children.title}>
              {s.children.body && (
                <p className="text-[0.98rem] leading-8 text-[#4f6557]">{s.children.body}</p>
              )}
            </SectionCard>

            <SectionCard title={s.changes.title}>
              {s.changes.body && (
                <p className="text-[0.98rem] leading-8 text-[#4f6557]">{s.changes.body}</p>
              )}
            </SectionCard>

            <SectionCard title={s.contact.title}>
              <p className="mb-5 text-[0.98rem] leading-8 text-[#4f6557]">{s.contact.intro}</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-[#dbe9de] bg-[#f7fbf7] p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#6e8a77]">
                    {locale === "ar" ? "الجهة" : "Organisation"}
                  </div>
                  <div className="mt-2 font-bold text-[#15452b]">
                    {s.contact.contact?.organisation}
                  </div>
                </div>
                <div className="rounded-2xl border border-[#dbe9de] bg-[#f7fbf7] p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#6e8a77]">
                    {locale === "ar" ? "الموقع" : "Location"}
                  </div>
                  <div className="mt-2 whitespace-pre-line font-bold text-[#15452b]">
                    {s.contact.contact?.location}
                  </div>
                </div>
                <div className="rounded-2xl border border-[#dbe9de] bg-[#f7fbf7] p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#6e8a77]">
                    {locale === "ar" ? "البريد الإلكتروني" : "Email"}
                  </div>
                  <a
                    href={`mailto:${s.contact.contact?.email}`}
                    className="mt-2 block font-bold text-[#138144] underline decoration-[#8fd7a9] underline-offset-4"
                  >
                    {s.contact.contact?.email}
                  </a>
                </div>
                <div className="rounded-2xl border border-[#dbe9de] bg-[#f7fbf7] p-4 sm:col-span-2">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#6e8a77]">
                    {locale === "ar" ? "الموقع الإلكتروني" : "Website"}
                  </div>
                  <a
                    href={`https://${s.contact.contact?.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 block font-bold text-[#138144] underline decoration-[#8fd7a9] underline-offset-4"
                  >
                    {s.contact.contact?.website}
                  </a>
                </div>
              </div>
              <p className="mt-2 text-[0.98rem] leading-8 text-[#4f6557]">
                {c.footerNote}
              </p>
            </SectionCard>

          </div>
        </main>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
